import React, { useMemo } from "react";
import { gbp, pct, dateShort } from "../lib/format";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Button } from "./ui/button";
import { Filter, X } from "lucide-react";

export const AssetsTable = ({ investments, filterSector, onClearFilter }) => {
  const filtered = useMemo(() => {
    if (!filterSector) return investments || [];
    return (investments || []).filter((inv) => inv.property_asset_type === filterSector);
  }, [investments, filterSector]);

  if (!investments || investments.length === 0) {
    return (
      <div
        className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center"
        data-testid="assets-empty-state"
      >
        <div className="overline">My Assets</div>
        <h3 className="mt-2 font-display text-xl font-semibold text-slate-900">No holdings yet</h3>
        <p className="mt-2 text-sm text-slate-500">
          Browse the marketplace to allocate capital into UK commercial real estate.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="assets-table-wrapper">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
        <div>
          <div className="overline">Positions</div>
          <h3 className="mt-1 font-display text-xl font-semibold text-slate-900">My Assets</h3>
        </div>
        <div className="flex items-center gap-3">
          {filterSector && (
            <span
              className="inline-flex items-center gap-2 rounded-sm px-3 py-1 text-xs font-semibold uppercase tracking-widest"
              style={{ background: "rgba(212,175,55,0.14)", color: "#8B6F1D" }}
              data-testid="assets-filter-chip"
            >
              <Filter className="h-3 w-3" /> Filter · {filterSector}
              {onClearFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearFilter}
                  className="ml-1 h-5 w-5 p-0 text-slate-600 hover:bg-transparent hover:text-slate-900"
                  data-testid="assets-clear-filter"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </span>
          )}
          <div className="text-xs text-slate-500 font-mono-num" data-testid="assets-count">
            {filtered.length} {filtered.length === 1 ? "position" : "positions"}
            {filterSector && ` of ${investments.length}`}
          </div>
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="p-10 text-center" data-testid="assets-filter-empty">
          <div className="overline">Filtered</div>
          <h3 className="mt-2 font-display text-lg font-semibold text-slate-900">
            No {filterSector} holdings
          </h3>
          <p className="mt-2 text-sm text-slate-500">
            You have no positions in the {filterSector?.toLowerCase()} sector. Clear the filter to see all holdings.
          </p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="pl-6 uppercase tracking-widest text-[11px] text-slate-500">Property</TableHead>
              <TableHead className="uppercase tracking-widest text-[11px] text-slate-500">Location</TableHead>
              <TableHead className="uppercase tracking-widest text-[11px] text-slate-500">Type</TableHead>
              <TableHead className="text-right uppercase tracking-widest text-[11px] text-slate-500">Tokens</TableHead>
              <TableHead className="text-right uppercase tracking-widest text-[11px] text-slate-500">Invested</TableHead>
              <TableHead className="text-right uppercase tracking-widest text-[11px] text-slate-500">Yield</TableHead>
              <TableHead className="text-right uppercase tracking-widest text-[11px] text-slate-500">Monthly Payout</TableHead>
              <TableHead className="pr-6 text-right uppercase tracking-widest text-[11px] text-slate-500">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((inv) => (
              <TableRow key={inv.id} data-testid={`asset-row-${inv.id}`} className="hover:bg-slate-50/60">
                <TableCell className="pl-6 font-medium text-slate-900">{inv.property_name}</TableCell>
                <TableCell className="text-slate-600">{inv.property_city}</TableCell>
                <TableCell className="text-slate-600">{inv.property_asset_type}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-900">{Math.round(inv.tokens).toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-900">{gbp(inv.amount_gbp)}</TableCell>
                <TableCell className="text-right font-mono-num" style={{ color: "#D4AF37" }}>{pct(inv.yield_pct)}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-900">{gbp(inv.monthly_payout_gbp, { decimals: 2 })}</TableCell>
                <TableCell className="pr-6 text-right font-mono-num text-slate-500">{dateShort(inv.invested_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default AssetsTable;
